import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Tabs,
  Form,
  Input,
  InputNumber,
  Select,
  AutoComplete,
  Switch,
  Upload,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Tooltip,
  Spin,
  Empty,
  Alert,
  Image,
  message,
} from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../config/api.js";
import ProductImageGallery from "./ProductImageGallery";
import { useAuth } from "../context/AuthContext";

const API_URL = API_CONFIG.BASE_URL;
// Galerie d'images supplementaires par declinaison (en plus de l'image principale),
// alignee sur les produits classiques mais plafonnee a 5 pour les variantes.
const VARIANT_GALLERY_MAX_IMAGES = 5;
const { Option } = Select;
const { Text } = Typography;

const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value) || 0);

const hasVariantStock = (variant) =>
  Number(
    variant?.absolute_current_stock ??
      Math.abs(Number(variant?.current_stock) || 0)
  ) > 0.000001;

/**
 * Modal de GESTION des declinaisons d'un produit a variantes existant.
 *
 * Respecte les regles du module produits :
 *  - Stock initial (opening_stock) IMMUABLE : affiche en lecture seule, jamais modifie.
 *  - Stock actuel : lecture seule (passe par les ajustements de stock).
 *  - Parent edite via PUT /:id/variant-parent (pas de product_details parasite).
 *  - Declinaisons editees via PUT /:parentId/variants/:childId (prix/taxe/seuil/codes/image).
 *  - Ajout via POST /:id/variants (creation => opening_stock autorise).
 *  - Activation/desactivation via les routes PATCH dediees (permissions et
 *    controle du stock tous magasins), avec trace d'audit.
 *
 * Props : open, onClose, onSuccess, parentId, categories, taxes.
 */
const VariantManagerModal = ({
  open,
  onClose,
  onSuccess,
  parentId,
  categories = [],
  taxes = [],
}) => {
  const [parentForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [axisForm] = Form.useForm();
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(false);
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [activeTab, setActiveTab] = useState("variants");

  // Edition parent
  const [savingParent, setSavingParent] = useState(false);
  const [parentImageFile, setParentImageFile] = useState(null);
  const [parentImagePreview, setParentImagePreview] = useState(null);
  const [removeParentImage, setRemoveParentImage] = useState(false);

  // Edition declinaisons : etat editable indexe par id enfant
  const [childEdits, setChildEdits] = useState({});
  const [savingChildId, setSavingChildId] = useState(null);
  const [togglingChildId, setTogglingChildId] = useState(null);
  const [retiringChildId, setRetiringChildId] = useState(null);

  // Ajout d'une declinaison
  const [addOpen, setAddOpen] = useState(false);
  const [addImageFile, setAddImageFile] = useState(null);
  const [addImagePreview, setAddImagePreview] = useState(null);
  const [addingVariant, setAddingVariant] = useState(false);

  // Ajout d'un axe sur un produit existant (option A)
  const [axisOpen, setAxisOpen] = useState(false);
  const [addingAxis, setAddingAxis] = useState(false);

  const axes = useMemo(
    () => (Array.isArray(parent?.variant_attributes) ? parent.variant_attributes : []),
    [parent]
  );
  const canDeleteVariant = hasPermission(
    "Gestion Commerciale.Produits.Produits.delete"
  );
  const canDisableVariant = hasPermission(
    "Gestion Commerciale.Produits.Produits.disable"
  );
  const canRestoreVariant = hasPermission(
    "Gestion Commerciale.Produits.Produits.restore"
  );

  const fetchData = async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/produits/${parentId}/variants`
      );
      setParent(data.parent || null);
      setChildren(data.variants || []);
      // Initialiser l'etat editable des declinaisons
      const edits = {};
      (data.variants || []).forEach((v) => {
        edits[v.id] = {
          item_code: v.item_code || "",
          barcode_value: v.barcode_value || "",
          purchase_price: v.purchase_price ?? 0,
          sales_price: v.sales_price ?? 0,
          tax_id: v.tax_id ?? null,
          stock_quantitiy_alert: v.stock_quantitiy_alert ?? 0,
          imageFile: null,
          imagePreview: null,
        };
      });
      setChildEdits(edits);
      // Pre-remplir le formulaire parent
      parentForm.setFieldsValue({
        name: data.parent?.name,
        category_id: data.parent?.category_id ?? undefined,
        barcode_symbology: data.parent?.barcode_symbology || "CODE128",
        online_visibility: Boolean(data.parent?.online_visibility),
        description: data.parent?.description || "",
      });
      setParentImageFile(null);
      setParentImagePreview(null);
      setRemoveParentImage(false);
    } catch (err) {
      message.error(
        err?.response?.data?.error ||
          "Erreur lors du chargement des déclinaisons"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && parentId) {
      setActiveTab("variants");
      fetchData();
    }
    if (!open) {
      setParent(null);
      setChildren([]);
      setChildEdits({});
      setAddOpen(false);
      setAxisOpen(false);
      setParentImageFile(null);
      setParentImagePreview(null);
      setRemoveParentImage(false);
      setAddImageFile(null);
      setAddImagePreview(null);
      parentForm.resetFields();
      addForm.resetFields();
      axisForm.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, parentId]);

  const updateChildEdit = (id, field, value) => {
    setChildEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  // ----- Enregistrement parent -----
  const handleSaveParent = async () => {
    let values;
    try {
      values = await parentForm.validateFields();
    } catch (e) {
      return;
    }
    setSavingParent(true);
    try {
      const fd = new FormData();
      fd.append("name", values.name);
      fd.append("category_id", values.category_id ?? "");
      fd.append("barcode_symbology", values.barcode_symbology || "CODE128");
      fd.append("online_visibility", values.online_visibility ? "1" : "0");
      fd.append("description", values.description ?? "");
      if (parentImageFile) {
        fd.append("image", parentImageFile);
      } else if (removeParentImage) {
        fd.append("remove_image", "true");
      }
      await axios.put(
        `${API_URL}/api/produits/${parentId}/variant-parent`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      message.success("Informations du produit mises à jour");
      await fetchData();
      onSuccess?.();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.field) {
        parentForm.setFields([{ name: data.field === "nom_produit" ? "name" : data.field, errors: [data.error] }]);
      }
      message.error(data?.error || "Erreur lors de l'enregistrement");
    } finally {
      setSavingParent(false);
    }
  };

  // ----- Ajout d'un axe (valeur commune sur les déclinaisons existantes) -----
  const handleAddAxis = async () => {
    let values;
    try {
      values = await axisForm.validateFields();
    } catch (_error) {
      return;
    }

    setAddingAxis(true);
    try {
      const defaultValue = String(values.default_value || "").trim();
      const additionalValues = Array.isArray(values.values) ? values.values : [];
      await axios.post(`${API_URL}/api/produits/${parentId}/variant-axes`, {
        axis: {
          name: String(values.name || "").trim(),
          values: [defaultValue, ...additionalValues],
        },
        default_value: defaultValue,
      });
      message.success(
        `Nouvel axe ajouté aux ${children.length} déclinaison(s) existante(s)`
      );
      setAxisOpen(false);
      axisForm.resetFields();
      await fetchData();
      onSuccess?.();
    } catch (err) {
      message.error(
        err?.response?.data?.error || "Erreur lors de l'ajout du nouvel axe"
      );
    } finally {
      setAddingAxis(false);
    }
  };

  // ----- Enregistrement d'une declinaison -----
  const handleSaveChild = async (child) => {
    const edit = childEdits[child.id];
    if (!edit) return;
    if (
      edit.sales_price === null ||
      edit.sales_price === undefined ||
      edit.sales_price === ""
    ) {
      message.error("Le prix de vente est requis");
      return;
    }
    setSavingChildId(child.id);
    try {
      const fd = new FormData();
      fd.append("item_code", edit.item_code ?? "");
      fd.append("barcode_value", edit.barcode_value ?? "");
      fd.append("purchase_price", edit.purchase_price ?? 0);
      fd.append("sales_price", edit.sales_price ?? 0);
      fd.append("tax_id", edit.tax_id ?? "");
      fd.append("stock_quantitiy_alert", edit.stock_quantitiy_alert ?? 0);
      if (edit.imageFile) fd.append("image", edit.imageFile);
      await axios.put(
        `${API_URL}/api/produits/${parentId}/variants/${child.id}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      message.success(`Déclinaison « ${child.variant_label || child.name} » enregistrée`);
      await fetchData();
      onSuccess?.();
    } catch (err) {
      const data = err?.response?.data;
      message.error(data?.error || "Erreur lors de l'enregistrement de la déclinaison");
    } finally {
      setSavingChildId(null);
    }
  };

  // ----- Activation / desactivation (reversible) -----
  const updateChildStatus = async (child, nextStatus) => {
    const action = nextStatus === "disabled" ? "disable" : "restore";
    setTogglingChildId(child.id);
    try {
      await axios.patch(
        `${API_URL}/api/produits/${parentId}/variants/${child.id}/${action}`,
        {
          reason:
            nextStatus === "disabled"
              ? "Déclinaison sans stock retirée via le gestionnaire"
              : "Réactivation via le gestionnaire de déclinaisons",
        }
      );
      message.success(
        nextStatus === "disabled"
          ? "Déclinaison désactivée"
          : "Déclinaison réactivée"
      );
      await fetchData();
      onSuccess?.();
    } catch (err) {
      message.error(
        err?.response?.data?.error || "Erreur lors du changement de statut"
      );
      throw err;
    } finally {
      setTogglingChildId(null);
    }
  };

  const handleToggleChildStatus = async (child) => {
    const isActive = child.status === "actif" || child.status === "active";
    if (!isActive) {
      try {
        await updateChildStatus(child, "actif");
      } catch (_error) {
        // Le message d'erreur est affiche par updateChildStatus.
      }
      return;
    }

    if (hasVariantStock(child)) {
      message.warning(
        "Le stock doit être nul dans tous les établissements avant de désactiver cette déclinaison"
      );
      return;
    }

    Modal.confirm({
      title: "Désactiver cette déclinaison ?",
      icon: <ExclamationCircleOutlined />,
      content:
        "Elle ne sera plus proposée dans les nouvelles transactions, mais son historique sera conservé et elle pourra être réactivée.",
      okText: "Désactiver",
      cancelText: "Annuler",
      onOk: () => updateChildStatus(child, "disabled"),
    });
  };

  // ----- Suppression protegee d'une declinaison -----
  const handleDeleteChild = async (child) => {
    setRetiringChildId(child.id);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/produits/${parentId}/variants/${child.id}/retirement-check`
      );
      const retirement = data?.retirement;
      if (!retirement?.stock?.is_zero) {
        message.warning(
          "Suppression impossible : le stock doit être nul dans tous les établissements"
        );
        return;
      }

      if (retirement.history?.exists) {
        const historyTotal = Number(retirement.history.total) || 0;
        const isActive =
          child.status === "actif" || child.status === "active";
        const modalConfig = {
          title: "Cette déclinaison possède déjà un historique",
          icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
          content: (
            <Space direction="vertical" size={6}>
              <Text>
                {historyTotal} référence(s) historique(s) ont été trouvées dans
                les ventes, achats ou mouvements de stock.
              </Text>
              <Text type="secondary">
                La suppression est bloquée pour préserver ces transactions. {" "}
                {isActive
                  ? "La désactivation la retirera des nouvelles opérations sans modifier l'historique."
                  : "Cette déclinaison est déjà désactivée et son historique reste intact."}
              </Text>
              {isActive && !canDisableVariant && (
                <Text type="secondary">
                  Votre rôle ne dispose pas de la permission de désactivation.
                </Text>
              )}
            </Space>
          ),
        };
        if (isActive && canDisableVariant) {
          Modal.confirm({
            ...modalConfig,
            okText: "Désactiver",
            cancelText: "Conserver active",
            onOk: () => updateChildStatus(child, "disabled"),
          });
        } else {
          Modal.info({
            ...modalConfig,
            okText: "Fermer",
          });
        }
        return;
      }

      if (retirement.is_last_variant) {
        Modal.info({
          title: "Dernière déclinaison du produit",
          content:
            "Elle ne peut pas être supprimée séparément. Utilisez l'action de suppression du produit parent, ou désactivez cette déclinaison.",
          okText: "Compris",
        });
        return;
      }

      if (retirement.can_delete && !canDeleteVariant) {
        if (!canDisableVariant) {
          message.warning(
            "Vous n'avez pas la permission de supprimer ou désactiver cette déclinaison"
          );
          return;
        }
        Modal.confirm({
          title: "Permission de suppression requise",
          content:
            "Vous pouvez toutefois désactiver cette déclinaison sans supprimer ses données.",
          okText: "Désactiver",
          cancelText: "Annuler",
          onOk: () => updateChildStatus(child, "disabled"),
        });
        return;
      }

      if (!retirement.can_delete) {
        message.warning("Cette déclinaison ne peut pas être supprimée");
        return;
      }

      Modal.confirm({
        title: `Supprimer la déclinaison « ${
          child.variant_label || child.name
        } » ?`,
        icon: <ExclamationCircleOutlined />,
        content:
          "Aucun stock ni historique n'a été trouvé. La déclinaison sera retirée, sans toucher aux autres déclinaisons du produit.",
        okText: "Supprimer",
        okButtonProps: { danger: true },
        cancelText: "Annuler",
        onOk: async () => {
          setRetiringChildId(child.id);
          try {
            await axios.delete(
              `${API_URL}/api/produits/${parentId}/variants/${child.id}`,
              {
                data: {
                  reason: "Suppression d'une déclinaison sans stock ni historique",
                },
              }
            );
            message.success("Déclinaison supprimée");
            await fetchData();
            onSuccess?.();
          } catch (err) {
            message.error(
              err?.response?.data?.error ||
                "Erreur lors de la suppression de la déclinaison"
            );
            throw err;
          } finally {
            setRetiringChildId(null);
          }
        },
      });
    } catch (err) {
      message.error(
        err?.response?.data?.error ||
          "Impossible de vérifier le stock et l'historique"
      );
    } finally {
      setRetiringChildId(null);
    }
  };

  // ----- Ajout d'une declinaison -----
  const handleAddVariant = async () => {
    let values;
    try {
      values = await addForm.validateFields();
    } catch (e) {
      return;
    }
    // Construire variant_values a partir des axes
    const variantValues = {};
    axes.forEach((axis) => {
      const v = values[`axis_${axis.name}`];
      if (v) variantValues[axis.name] = v;
    });
    if (Object.keys(variantValues).length !== axes.length) {
      message.error("Renseignez une valeur pour chaque axe");
      return;
    }
    setAddingVariant(true);
    try {
      const fd = new FormData();
      fd.append("variant_values", JSON.stringify(variantValues));
      fd.append("purchase_price", values.purchase_price ?? 0);
      fd.append("sales_price", values.sales_price ?? 0);
      fd.append("tax_id", values.tax_id ?? "");
      fd.append("opening_stock", values.opening_stock ?? 0);
      fd.append("stock_quantitiy_alert", values.stock_quantitiy_alert ?? 0);
      if (values.item_code) fd.append("item_code", values.item_code);
      if (values.barcode_value) fd.append("barcode_value", values.barcode_value);
      if (addImageFile) fd.append("image", addImageFile);
      await axios.post(`${API_URL}/api/produits/${parentId}/variants`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      message.success("Déclinaison ajoutée");
      setAddOpen(false);
      addForm.resetFields();
      setAddImageFile(null);
      setAddImagePreview(null);
      await fetchData();
      onSuccess?.();
    } catch (err) {
      const data = err?.response?.data;
      message.error(data?.error || "Erreur lors de l'ajout de la déclinaison");
    } finally {
      setAddingVariant(false);
    }
  };

  const childColumns = [
    {
      title: "Déclinaison",
      dataIndex: "variant_label",
      fixed: "left",
      width: 130,
      render: (label, row) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{label || "—"}</Tag>
          {row.status !== "actif" && row.status !== "active" && (
            <Tag color="red" style={{ marginTop: 4 }}>
              Désactivée
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "SKU",
      dataIndex: "item_code",
      width: 140,
      render: (_, row) => (
        <Input
          size="small"
          value={childEdits[row.id]?.item_code}
          onChange={(e) => updateChildEdit(row.id, "item_code", e.target.value)}
        />
      ),
    },
    {
      title: "Code-barres",
      dataIndex: "barcode_value",
      width: 140,
      render: (_, row) => (
        <Input
          size="small"
          value={childEdits[row.id]?.barcode_value}
          onChange={(e) =>
            updateChildEdit(row.id, "barcode_value", e.target.value)
          }
        />
      ),
    },
    {
      title: "Prix achat",
      dataIndex: "purchase_price",
      width: 110,
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={childEdits[row.id]?.purchase_price}
          onChange={(v) => updateChildEdit(row.id, "purchase_price", v)}
        />
      ),
    },
    {
      title: "Prix vente",
      dataIndex: "sales_price",
      width: 110,
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={childEdits[row.id]?.sales_price}
          onChange={(v) => updateChildEdit(row.id, "sales_price", v)}
        />
      ),
    },
    {
      title: "Taxe",
      dataIndex: "tax_id",
      width: 130,
      render: (_, row) => (
        <Select
          size="small"
          allowClear
          style={{ width: "100%" }}
          placeholder="Aucune"
          value={childEdits[row.id]?.tax_id ?? undefined}
          onChange={(v) => updateChildEdit(row.id, "tax_id", v ?? null)}
        >
          {taxes.map((t) => (
            <Option key={t.id} value={t.id}>
              {t.name} ({t.rate}%)
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: (
        <Tooltip title="Le stock initial est immuable après création (règle de cohérence du stock).">
          Stock initial
        </Tooltip>
      ),
      dataIndex: "opening_stock",
      width: 95,
      render: (v) => <Text type="secondary">{formatNumber(v)}</Text>,
    },
    {
      title: (
        <Tooltip title="Modifiable uniquement via les ajustements de stock.">
          Stock actuel
        </Tooltip>
      ),
      dataIndex: "current_stock",
      width: 95,
      render: (v, row) => {
        const totalStock = row.total_current_stock ?? v;
        const content = <Text>{formatNumber(totalStock)}</Text>;
        return Number(row.stock_locations_count) > 1 ? (
          <Tooltip title="Stock total de cette déclinaison dans tous les établissements">
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    },
    {
      title: "Seuil",
      dataIndex: "stock_quantitiy_alert",
      width: 90,
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={childEdits[row.id]?.stock_quantitiy_alert}
          onChange={(v) => updateChildEdit(row.id, "stock_quantitiy_alert", v)}
        />
      ),
    },
    {
      title: "Image",
      dataIndex: "imageFile",
      width: 110,
      render: (_, row) => {
        const preview = childEdits[row.id]?.imagePreview || row.image;
        return (
          <Space size={4}>
            {preview ? (
              <Image
                src={preview}
                alt={row.variant_label || ""}
                width={40}
                height={40}
                style={{ objectFit: "cover", borderRadius: 6 }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3C/svg%3E"
              />
            ) : null}
            <Upload
              beforeUpload={(file) => {
                updateChildEdit(row.id, "imageFile", file);
                updateChildEdit(
                  row.id,
                  "imagePreview",
                  URL.createObjectURL(file)
                );
                return false;
              }}
              maxCount={1}
              showUploadList={false}
              accept="image/*"
            >
              <Button size="small" icon={<UploadOutlined />}>
                {childEdits[row.id]?.imageFile
                  ? "Changer"
                  : row.image
                  ? "Changer"
                  : "Ajouter"}
              </Button>
            </Upload>
          </Space>
        );
      },
    },
    {
      title: "Actif",
      dataIndex: "status",
      fixed: "right",
      width: 70,
      render: (status, row) => {
        const isActive = status === "actif" || status === "active";
        const blockedByStock = isActive && hasVariantStock(row);
        const disabled = isActive
          ? blockedByStock || !canDisableVariant
          : !canRestoreVariant;
        const title = blockedByStock
          ? "Ramenez d'abord le stock à zéro dans tous les établissements"
          : isActive && !canDisableVariant
          ? "Permission de désactivation requise"
          : !isActive && !canRestoreVariant
          ? "Permission de restauration requise"
          : isActive
          ? "Désactiver cette déclinaison"
          : "Réactiver cette déclinaison";
        return (
          <Tooltip title={title}>
            <span>
              <Switch
                size="small"
                checked={isActive}
                disabled={disabled}
                loading={togglingChildId === row.id}
                onChange={() => handleToggleChildStatus(row)}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      fixed: "right",
      width: 100,
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="Enregistrer cette déclinaison">
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              loading={savingChildId === row.id}
              onClick={() => handleSaveChild(row)}
            />
          </Tooltip>
          {(canDeleteVariant || canDisableVariant) && (
            <Tooltip
              title={
                hasVariantStock(row)
                  ? "Stock nul requis dans tous les établissements"
                  : "Supprimer si aucun historique, sinon désactiver"
              }
            >
              <span>
                <Button
                  danger
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={hasVariantStock(row)}
                  loading={retiringChildId === row.id}
                  onClick={() => handleDeleteChild(row)}
                />
              </span>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={
        <Space>
          <AppstoreOutlined />
          <span>
            Gérer les déclinaisons
            {parent?.name ? ` — ${parent.name}` : ""}
          </span>
        </Space>
      }
      onCancel={onClose}
      footer={null}
      width={1180}
      destroyOnClose
      maskClosable={false}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            tab={`Déclinaisons${children.length ? ` (${children.length})` : ""}`}
            key="variants"
          >
            <Space
              style={{ marginBottom: 12, justifyContent: "space-between", width: "100%" }}
            >
              <Button icon={<ReloadOutlined />} onClick={fetchData}>
                Actualiser
              </Button>
              <Space>
                <Tooltip
                  title={
                    axes.length >= 3
                      ? "Trois axes maximum sont autorisés"
                      : "Une valeur commune sera appliquée aux déclinaisons actuelles"
                  }
                >
                  <span>
                    <Button
                      icon={<PlusOutlined />}
                      disabled={axes.length >= 3}
                      onClick={() => {
                        axisForm.resetFields();
                        setAxisOpen(true);
                      }}
                    >
                      Ajouter un axe
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    if (!axes.length) {
                      message.warning(
                        "Aucun axe défini. Ajoutez d'abord les axes dans l'onglet « Informations du produit »."
                      );
                      setActiveTab("parent");
                      return;
                    }
                    addForm.resetFields();
                    setAddImageFile(null);
                    setAddImagePreview(null);
                    setAddOpen(true);
                  }}
                >
                  Ajouter une déclinaison
                </Button>
              </Space>
            </Space>

            {children.length ? (
              <Table
                size="small"
                rowKey="id"
                dataSource={children}
                columns={childColumns}
                pagination={false}
                scroll={{ x: 1320 }}
                expandable={{
                  // Galerie d'images supplementaires par declinaison (jusqu'a 5),
                  // en plus de l'image principale de la colonne "Image".
                  expandedRowRender: (row) => (
                    <div style={{ padding: "8px 4px" }}>
                      <Text strong>
                        Galerie d'images supplémentaires (jusqu'à{" "}
                        {VARIANT_GALLERY_MAX_IMAGES})
                      </Text>
                      <p
                        style={{
                          margin: "4px 0 12px",
                          color: "rgba(0,0,0,0.45)",
                          fontSize: 12,
                        }}
                      >
                        Ces images viennent en complément de l'image principale de
                        la déclinaison « {row.variant_label || row.name} ».
                      </p>
                      <ProductImageGallery
                        productId={row.id}
                        maxImages={VARIANT_GALLERY_MAX_IMAGES}
                      />
                    </div>
                  ),
                  rowExpandable: () => true,
                }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Aucune déclinaison pour ce produit."
              />
            )}

            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Astuces"
              description={
                <span>
                  Le stock initial est figé à la création (règle de cohérence) ;
                  le stock actuel évolue uniquement via les ventes, achats et
                  ajustements de stock. Dépliez une déclinaison (flèche à gauche)
                  pour gérer sa galerie d'images supplémentaires (jusqu'à{" "}
                  {VARIANT_GALLERY_MAX_IMAGES}).
                </span>
              }
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Informations du produit" key="parent">
            <Form form={parentForm} layout="vertical">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 16,
                }}
              >
                <Form.Item
                  name="name"
                  label="Nom du produit"
                  rules={[{ required: true, message: "Le nom est requis" }]}
                >
                  <Input placeholder="Nom du produit" />
                </Form.Item>
                <Form.Item name="category_id" label="Catégorie">
                  <Select
                    placeholder="Aucune"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {categories.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="barcode_symbology" label="Symbole code-barres">
                  <Select>
                    <Option value="CODE128">CODE 128</Option>
                    <Option value="EAN13">EAN-13</Option>
                    <Option value="CODE39">CODE 39</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="online_visibility"
                  label="Visible en ligne (boutique)"
                  valuePropName="checked"
                  tooltip="Propagé à toutes les déclinaisons pour garder le catalogue cohérent."
                >
                  <Switch checkedChildren="Oui" unCheckedChildren="Non" />
                </Form.Item>
              </div>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} placeholder="Description (optionnelle)" />
              </Form.Item>
              <Form.Item label="Image principale (partagée)">
                <Space align="start" wrap>
                  {(parentImagePreview ||
                    (parent?.image && !removeParentImage)) && (
                    <Image
                      src={parentImagePreview || parent.image}
                      alt="Aperçu image produit"
                      width={88}
                      height={88}
                      style={{
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                      }}
                    />
                  )}
                  <Space direction="vertical" size={6}>
                    <Upload
                      beforeUpload={(file) => {
                        setParentImageFile(file);
                        setParentImagePreview(URL.createObjectURL(file));
                        setRemoveParentImage(false);
                        return false;
                      }}
                      maxCount={1}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />}>
                        {parentImageFile
                          ? "Changer l'image"
                          : parent?.image
                          ? "Remplacer l'image"
                          : "Ajouter une image"}
                      </Button>
                    </Upload>
                    {parentImageFile && (
                      <Button
                        size="small"
                        onClick={() => {
                          setParentImageFile(null);
                          setParentImagePreview(null);
                        }}
                      >
                        Annuler le changement
                      </Button>
                    )}
                    {parent?.image && !parentImageFile && (
                      <Button
                        size="small"
                        danger
                        type={removeParentImage ? "primary" : "default"}
                        onClick={() => setRemoveParentImage((v) => !v)}
                      >
                        {removeParentImage
                          ? "Suppression prévue"
                          : "Retirer l'image"}
                      </Button>
                    )}
                  </Space>
                </Space>
              </Form.Item>

              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  <Space
                    wrap
                    style={{ justifyContent: "space-between", width: "100%" }}
                  >
                    <span>Axes de déclinaison ({axes.length}/3)</span>
                    <Tooltip
                      title={
                        axes.length >= 3
                          ? "Trois axes maximum sont autorisés"
                          : "Ajouter un axe sans recréer les déclinaisons"
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<PlusOutlined />}
                          disabled={axes.length >= 3}
                          onClick={() => {
                            axisForm.resetFields();
                            setAxisOpen(true);
                          }}
                        >
                          Ajouter un axe
                        </Button>
                      </span>
                    </Tooltip>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Space wrap>
                      {axes.map((a) => (
                        <Tag key={a.name} color="geekblue">
                          {a.name}: {(a.values || []).join(", ")}
                        </Tag>
                      ))}
                    </Space>
                    <Text type="secondary">
                      Lors de l'ajout, une valeur commune est appliquée à toutes
                      les déclinaisons existantes. Leurs identifiants, stocks,
                      codes et historiques restent inchangés.
                    </Text>
                  </Space>
                }
              />

              <div style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={savingParent}
                  onClick={handleSaveParent}
                >
                  Enregistrer les informations
                </Button>
              </div>
            </Form>
          </Tabs.TabPane>
        </Tabs>
      )}

      {/* Sous-modal : ajout d'un axe avec valeur commune (option A) */}
      <Modal
        open={axisOpen}
        title="Ajouter un axe de déclinaison"
        onCancel={() => {
          setAxisOpen(false);
          axisForm.resetFields();
        }}
        onOk={handleAddAxis}
        okText="Ajouter l'axe"
        confirmLoading={addingAxis}
        destroyOnClose
        width={620}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Valeur commune pour les déclinaisons existantes"
          description={`La valeur initiale sera ajoutée aux ${children.length} déclinaison(s) actuelles dans une seule opération. Aucun stock ni historique ne sera recréé.`}
        />
        <Form form={axisForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nom du nouvel axe"
            rules={[
              { required: true, message: "Le nom de l'axe est requis" },
              { max: 64, message: "64 caractères maximum" },
            ]}
          >
            <Input placeholder="Ex : Dimension, Couleur, Matière" />
          </Form.Item>
          <Form.Item
            name="default_value"
            label="Valeur appliquée aux déclinaisons existantes"
            rules={[
              { required: true, message: "La valeur commune est requise" },
              { max: 100, message: "100 caractères maximum" },
            ]}
          >
            <Input placeholder="Ex : 2 places" />
          </Form.Item>
          <Form.Item
            name="values"
            label="Autres valeurs disponibles"
            tooltip="Facultatif : elles seront proposées lors de l'ajout de nouvelles déclinaisons."
          >
            <Select
              mode="tags"
              tokenSeparators={[",", ";"]}
              placeholder="Ex : 3 places, King size"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sous-modal : ajout d'une declinaison */}
      <Modal
        open={addOpen}
        title="Ajouter une déclinaison"
        onCancel={() => setAddOpen(false)}
        onOk={handleAddVariant}
        okText="Ajouter"
        confirmLoading={addingVariant}
        destroyOnClose
        width={620}
      >
        <Form form={addForm} layout="vertical">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 12,
            }}
          >
            {axes.map((axis) => (
              <Form.Item
                key={axis.name}
                name={`axis_${axis.name}`}
                label={axis.name}
                rules={[{ required: true, message: `${axis.name} requis` }]}
              >
                <AutoComplete
                  placeholder={`Ex: ${(axis.values || [])[0] || axis.name}`}
                  options={(axis.values || []).map((v) => ({ value: v }))}
                  filterOption={(input, option) =>
                    String(option?.value ?? "")
                      .toLowerCase()
                      .includes(String(input).toLowerCase())
                  }
                />
              </Form.Item>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 12,
            }}
          >
            <Form.Item
              name="purchase_price"
              label="Prix d'achat"
              initialValue={0}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="sales_price"
              label="Prix de vente"
              rules={[{ required: true, message: "Prix de vente requis" }]}
              initialValue={0}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="tax_id" label="Taxe">
              <Select allowClear placeholder="Aucune">
                {taxes.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.name} ({t.rate}%)
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="opening_stock"
              label="Stock initial"
              initialValue={0}
              tooltip="Défini à la création, immuable ensuite."
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="stock_quantitiy_alert"
              label="Seuil d'alerte"
              initialValue={0}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="item_code" label="SKU (auto si vide)">
              <Input placeholder="Auto" />
            </Form.Item>
            <Form.Item name="barcode_value" label="Code-barres (= SKU si vide)">
              <Input placeholder="= SKU si vide" />
            </Form.Item>
            <Form.Item label="Image">
              <Space align="start" wrap>
                {addImagePreview && (
                  <Image
                    src={addImagePreview}
                    alt="Aperçu déclinaison"
                    width={64}
                    height={64}
                    style={{ objectFit: "cover", borderRadius: 8 }}
                  />
                )}
                <Upload
                  beforeUpload={(file) => {
                    setAddImageFile(file);
                    setAddImagePreview(URL.createObjectURL(file));
                    return false;
                  }}
                  maxCount={1}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />}>
                    {addImageFile ? "Changer l'image" : "Ajouter une image"}
                  </Button>
                </Upload>
                {addImageFile && (
                  <Button
                    size="small"
                    onClick={() => {
                      setAddImageFile(null);
                      setAddImagePreview(null);
                    }}
                  >
                    Retirer
                  </Button>
                )}
              </Space>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </Modal>
  );
};

export default VariantManagerModal;
